import { founder } from '../../data/content'
import type { BrandKit, MarketingDesign } from '../../core/types/marketing'

/**
 * Startinhoud van het marketingpakket: kant-en-klare ontwerpen en merkteksten.
 * Het beheer begint hiermee zolang er niets is opgeslagen; met een backend komt
 * dit uit GET /admin/content (AdminContent.marketing). Tekst mag {tokens}
 * bevatten ({firstName}, {years}, {phone}, …) — die volgen de instellingen.
 */

const base = { eyebrow: '', accent: '', body: '', points: [] as string[], cta: '', image: '', qrUrl: '', caption: '', hashtags: [] as string[] }

export const designs: MarketingDesign[] = [
  {
    ...base,
    id: 'lancering-post',
    name: 'Nieuwe webshop — aankondiging',
    campaign: 'Nieuwe website',
    template: 'photo',
    palette: 'klei',
    image: founder.withProducts,
    eyebrow: 'Nieuw',
    headline: 'Mijn webshop is verhuisd.',
    accent: 'Mijn advies niet.',
    body: 'Dezelfde producten en dezelfde eerlijke blik. Nu met huidadvies in twee minuten.',
    cta: 'beautybewust.nl',
    caption:
      'Na {years} jaar in de salon heb ik mijn webshop opnieuw opgebouwd. Zoals ik ook behandel: rustig, overzichtelijk en eerlijk.\n\nJe vindt er geen plank vol potjes, maar een kleine selectie die ik zelf gebruik en adviseer. Twijfel je wat bij jouw huid past? Doe het huidadvies: vier vragen, en ik stel een protocol voor je samen.\n\nLink in bio.',
    hashtags: ['#beautybewust', '#huidspecialist', '#amsterdamoost', '#huidverzorging'],
  },
  {
    ...base,
    id: 'lancering-story',
    name: 'Huidadvies — story',
    campaign: 'Nieuwe website',
    template: 'story',
    palette: 'inkt',
    image: founder.portrait,
    eyebrow: 'Nieuw op de site',
    headline: 'Welke routine past',
    accent: 'bij jouw huid?',
    body: 'Vier vragen, één protocol. Persoonlijk nagekeken door mij.',
    cta: 'Doe het huidadvies',
    caption: 'Plak hier de linksticker naar beautybewust.nl/routines (daar start het huidadvies)',
  },
  {
    ...base,
    id: 'citaat-minder',
    name: 'Citaat — minder potjes',
    campaign: 'Huidfilosofie',
    template: 'quote',
    palette: 'blush',
    image: founder.portraitPalm,
    headline: 'Een goede routine telt drie of vier producten. Niet twaalf.',
    body: '{firstName}, huidspecialist',
    caption:
      'Ik zie het elke week in de salon: een badkamerplank vol, en een huid die niet weet waar ze aan toe is.\n\nMeer producten betekent niet meer resultaat. Wel meer kans dat ze elkaar in de weg zitten. Begin met de basis die klopt: reinigen, één werkzaam serum, een goede crème en SPF.\n\nWil je dat ik meekijk naar jouw plank? Stuur me een foto.',
    hashtags: ['#beautybewust', '#minderismeer', '#skincareroutine', '#huidadvies'],
  },
  {
    ...base,
    id: 'citaat-geduld',
    name: 'Citaat — 28 dagen',
    campaign: 'Huidfilosofie',
    template: 'quote',
    palette: 'salie',
    image: founder.atWork,
    headline: 'Je huid heeft geen haast. Geef haar 28 dagen.',
    body: '{firstName}, huidspecialist',
    caption:
      'Een huidcel doet er ongeveer 28 dagen over om van binnen naar buiten te komen. Daarom zeg ik altijd: oordeel pas na een maand.\n\nFrisser voelt vaak al na een week. Egaler zie je na vier weken. En pigment? Dat vraagt drie maanden geduld en elke dag SPF.',
    hashtags: ['#beautybewust', '#huidverzorging', '#huidspecialist'],
  },
  {
    ...base,
    id: 'tip-spf',
    name: 'Huidtip — vitamine C en SPF',
    campaign: 'Huidtips',
    template: 'tip',
    palette: 'zand',
    eyebrow: 'Huidtip',
    headline: 'Vitamine C werkt pas',
    accent: 'met SPF erbij.',
    body: 'Zonder bescherming poets je ’s middags weg wat je ’s ochtends opbouwt.',
    points: ['’s Ochtends: reinigen, vitamine C, crème', 'Altijd afsluiten met SPF 30 of hoger', 'Ook als het bewolkt is'],
    caption:
      'De vraag die ik het vaakst krijg over pigment: “Ik gebruik al maanden vitamine C, waarom zie ik niets?”\n\nBijna altijd ontbreekt de SPF. Vitamine C beschermt en verheldert, maar UV maakt elke dag nieuw pigment aan. Samen werken ze; los niet.\n\nBewaar deze post voor je volgende bestelling.',
    hashtags: ['#beautybewust', '#vitaminec', '#spf', '#pigmentatie', '#huidtip'],
  },
  {
    ...base,
    id: 'tip-retinal',
    name: 'Huidtip — retinal opbouwen',
    campaign: 'Huidtips',
    template: 'tip',
    palette: 'roos',
    eyebrow: 'Huidtip',
    headline: 'Retinal?',
    accent: 'Rustig opbouwen.',
    body: 'Een beetje schilfering is normaal. Branden niet: app me dan.',
    points: ['Week 1 en 2: twee avonden per week', 'Week 3 en 4: om de avond', 'Daarna elke avond, als je huid rustig blijft'],
    caption:
      'Vitamine A is het best onderzochte ingrediënt tegen huidveroudering. Maar wie te snel wil, krijgt een rode, trekkerige huid en stopt.\n\nBouw op zoals hierboven, gebruik overdag altijd SPF, en sla het over tijdens zwangerschap of borstvoeding. Twijfel je over de sterkte? Ik kijk graag met je mee.',
    hashtags: ['#beautybewust', '#retinal', '#medik8', '#antiageing', '#huidtip'],
  },
  {
    ...base,
    id: 'product-retinoate',
    name: 'Spotlight — r-Retinoate',
    campaign: 'Medik8',
    template: 'product',
    palette: 'blush',
    productId: 'medik8-retinoate-bestseller',
    eyebrow: 'Mijn persoonlijke favoriet',
    headline: 'r-Retinoate Day & Night',
    body: 'Vitamine A voor dag én nacht. Acht keer sterker dan retinol, en toch zacht.',
    cta: 'beautybewust.nl',
    caption:
      'Als ik er één zou mogen houden, is het deze. De r-Retinoate van Medik8 is een vitamine A die je ook overdag mag gebruiken: sterk, maar zo zacht dat ook een gevoelige huid eraan kan wennen.\n\nVanaf {freeShippingFrom} verzend ik gratis, en je krijgt altijd persoonlijk advies bij je bestelling.',
    hashtags: ['#beautybewust', '#medik8', '#retinoate', '#vitaminea', '#antiageing'],
  },
  {
    ...base,
    id: 'product-ctetra',
    name: 'Spotlight — C-Tetra',
    campaign: 'Medik8',
    template: 'product',
    palette: 'klei',
    productId: 'medik8-c-tetra-serum',
    eyebrow: 'Nieuw in de webshop',
    headline: 'C-Tetra',
    body: 'Vitamine C voor elke dag, ook voor een gevoelige huid. Voor een frisse, egale teint.',
    cta: 'beautybewust.nl',
    caption:
      'Nieuw in de webshop: de C-Tetra van Medik8. Een milde vitamine C die ook een gevoelige huid goed verdraagt.\n\n’s Ochtends na je toner, vóór je crème, en altijd afsluiten met SPF. Dan werkt hij het best tegen pigment.',
    hashtags: ['#beautybewust', '#medik8', '#ctetra', '#vitaminec'],
  },
  {
    ...base,
    id: 'protocol-pigment',
    name: 'Protocol — pigment',
    campaign: 'Protocollen',
    template: 'routine',
    palette: 'zand',
    routineId: 'pigment-protocol',
    eyebrow: 'Protocol',
    headline: 'Pigment Protocol',
    accent: 'in de juiste volgorde',
    body: 'Precies wat ik meegeef na een vitamine C behandeling.',
    cta: 'Met persoonlijk huidplan',
    caption:
      'Pigment houdt zich alleen rustig als reiniging, vitamine C en bescherming samen optrekken. Dit protocol is exact wat ik meegeef na een vitamine C behandeling in de salon.\n\nMet een persoonlijk huidplan en een opbouwschema voor de eerste zes weken.',
    hashtags: ['#beautybewust', '#pigmentatie', '#emerginc', '#skincareroutine'],
  },
  {
    ...base,
    id: 'consult-post',
    name: 'Online consult — badkamerplank',
    campaign: 'Huidadvies',
    template: 'photo',
    palette: 'salie',
    image: founder.withProducts,
    eyebrow: '{consultName} · {consultMinutes} minuten',
    headline: 'Neem je badkamerplank mee.',
    accent: 'Ik kijk mee.',
    body: 'Online huidconsult voor {consultPrice}. Wat werkt, wat mag weg en wat mist er nog.',
    cta: 'Plan in via beautybewust.nl',
    caption:
      'Je hoeft niet naar de salon te komen voor goed advies. In een videocall van {consultMinutes} minuten lopen we samen je badkamerplank door: wat werkt, wat mag weg en wat mist er nog.\n\nBestel je daarna een protocol, dan wordt het consult verrekend.',
    hashtags: ['#beautybewust', '#huidadvies', '#shelfie', '#huidspecialist'],
  },
  {
    ...base,
    id: 'flyer-salon',
    name: 'Flyer — de salon',
    campaign: 'Salon',
    template: 'flyer',
    palette: 'blush',
    image: founder.atWork,
    eyebrow: 'Huidsalon in {city}',
    headline: 'Eén cliënt tegelijk.',
    accent: 'Altijd door mij.',
    body: 'Gezichtsbehandelingen met bindweefselmassage, huidanalyse en een thuisroutine die klopt. Al {years} jaar.',
    points: ['Huidanalyse bij je eerste afspraak', 'Behandelingen van een uur, zonder haast', 'Je thuisroutine als huidplan in Mijn omgeving'],
    cta: 'Plan je afspraak op beautybewust.nl',
    qrUrl: 'https://beautybewust.nl/behandelingen',
  },
  {
    ...base,
    id: 'poster-medik8',
    name: 'Poster — Medik8 in de salon',
    campaign: 'Medik8',
    template: 'poster',
    palette: 'inkt',
    productId: 'medik8-c-tetra-serum',
    eyebrow: 'Nu in de salon en online',
    headline: 'Medik8',
    accent: 'vitamine A en C, rustig opgebouwd',
    body: 'Vraag naar een huidcheck. Ik help je kiezen welke sterkte bij jouw huid past.',
    qrUrl: 'https://beautybewust.nl/webshop',
  },
  {
    ...base,
    id: 'visitekaartje',
    name: 'Visitekaartje',
    campaign: 'Salon',
    template: 'card',
    palette: 'inkt',
    headline: '{name}',
    body: 'Huidspecialist & oprichter',
    points: ['{phone}', '{email}', '{street}, {locality}', 'beautybewust.nl'],
    qrUrl: 'https://beautybewust.nl',
  },
  {
    ...base,
    id: 'cadeaubon',
    name: 'Cadeaubon — behandeling',
    campaign: 'Cadeau',
    template: 'giftcard',
    palette: 'klei',
    eyebrow: 'Cadeaubon',
    headline: '€ 75',
    accent: 'voor een uur alleen voor jou',
    points: ['Voor', 'Van'],
    body: 'Tijd voor jezelf. Een behandeling waarin je huid, en jij, even helemaal op adem komt.',
    // Geldigheid volgt de instelling "Cadeaubon geldig".
    cta: 'Te besteden aan behandelingen en producten · {giftCardValidity}',
  },
  {
    ...base,
    id: 'nazorg',
    name: 'Nazorgkaart',
    campaign: 'Salon',
    template: 'aftercare',
    palette: 'zand',
    eyebrow: 'Na je behandeling',
    headline: 'De komende 48 uur',
    points: [
      'Geen make-up tot morgenochtend',
      'Niet sporten, geen sauna of zonnebank',
      '’s Avonds alleen milde reiniging en je crème',
      'Overdag SPF 30 of hoger, ook als het bewolkt is',
      'Drink genoeg water',
    ],
    body: 'Twijfel je ergens over? App me gerust: {phone}',
    cta: 'Je volgende afspraak',
  },
  {
    ...base,
    id: 'story-cadeau',
    name: 'Cadeaubon — story',
    campaign: 'Cadeau',
    template: 'story',
    palette: 'roos',
    image: founder.portraitPalm,
    eyebrow: 'Cadeau nodig?',
    headline: 'Geef tijd',
    accent: 'voor haar huid.',
    body: 'Een cadeaubon voor een behandeling of producten. Digitaal binnen een minuut, of op mooi papier uit de salon.',
    cta: 'Bestel via de link',
    caption: 'Plak hier de linksticker naar beautybewust.nl/webshop',
  },
  {
    ...base,
    id: 'nieuwsbrief',
    name: 'Nieuwsbriefkop — maandelijks',
    campaign: 'Nieuwsbrief',
    template: 'email',
    palette: 'klei',
    image: founder.atWork,
    eyebrow: 'Het Bewust Journal',
    headline: 'Wat je huid',
    accent: 'deze maand nodig heeft',
  },
]

// ——— Merkstijl & teksten ———

export const brandKit: BrandKit = {
  taglines: [
    'Minder potjes. Meer resultaat.',
    'Eén cliënt tegelijk. Altijd door mij.',
    'Eerlijk huidadvies, van {firstName} persoonlijk.',
    'Producten die samenwerken.',
  ],
  messages: [
    { title: 'Persoonlijk', text: 'Je spreekt altijd {firstName}: in de salon, op WhatsApp en bij je bestelling. Geen callcenter, geen chatbot.' },
    { title: 'Minder, maar goed', text: 'Drie of vier producten die samenwerken, in plaats van een plank vol potjes die elkaar in de weg zitten.' },
    { title: 'Eerlijk over resultaat', text: 'Wat je mag verwachten en wanneer. Een huid vernieuwt in ongeveer 28 dagen; beloftes van een week doen we niet.' },
    { title: 'Nooit korting, wel iets extra', text: 'Geen prijsacties. Wel een cadeau bij je bestelling, begeleiding en een persoonlijk huidplan.' },
  ],
  toneDo: [
    'Schrijf als {firstName}: “ik” en “je”',
    'Eén boodschap per bericht, kort en concreet',
    'Noem wat iemand merkt, en na hoeveel tijd',
    'Leg een vakwoord uit in één zin',
    'Eindig met één duidelijke stap: app me, plan in of lees verder',
  ],
  toneDont: [
    'Geen korting, “sale” of “op = op”',
    'Geen beloftes als “rimpels weg” of “in één week”',
    'Geen rijen uitroeptekens of emoji als opsomming',
    'Geen Engelse marketingwoorden als “glow-up”',
    'Geen hoofdletters voor nadruk',
  ],
  bios: [
    {
      label: 'Instagram-bio (max. 150 tekens)',
      text: 'Huidspecialist in {city} · {years}+ jaar\nEén cliënt tegelijk, altijd door mij\nHuidadvies in 2 minuten ↓',
    },
    {
      label: 'Google Bedrijfsprofiel',
      text: 'Beauty Bewust is de huidsalon van {name} in {city}. Al {years} jaar behandel ik één cliënt tegelijk, met gezichtsbehandelingen, bindweefselmassage en eerlijk advies over je thuisroutine. In de salon en online werk ik met emerginC en Medik8. Afspraken op afspraak; app of mail me gerust.',
    },
    {
      label: 'WhatsApp Business — begroeting',
      text: 'Hoi! Fijn dat je me appt. Ik ben waarschijnlijk met een cliënt bezig en reageer binnen {responseHours} uur. Gaat het over je huid? Stuur gerust alvast een foto bij daglicht, zonder make-up. — {firstName}',
    },
    {
      label: 'WhatsApp Business — afwezig',
      text: 'Dank je voor je bericht. Ik ben er even niet, maar lees alles zodra ik terug ben en reageer dan persoonlijk. Bestellen kan gewoon via beautybewust.nl. — {firstName}',
    },
    {
      label: 'E-mailhandtekening',
      text: '{name}\nHuidspecialist · Beauty Bewust\n{street}, {locality} · {phone}\nbeautybewust.nl',
    },
    {
      label: 'Review vragen (na een behandeling)',
      text: 'Wat fijn dat je er was vandaag. Als je een minuut hebt: een korte review helpt andere vrouwen enorm om de stap te zetten. Dank je wel! — {firstName}',
    },
  ],
  hashtags: [
    '#beautybewust',
    '#huidspecialist',
    '#amsterdamoost',
    '#huidverzorging',
    '#skincareroutine',
    '#gezichtsbehandeling',
    '#bindweefselmassage',
    '#medik8',
    '#emerginc',
    '#huidadvies',
  ],
  rhythm: [
    { moment: 'Maandag', what: 'Een huidtip of een vraag van een cliënt (post)' },
    { moment: 'Woensdag', what: 'Achter de schermen in de salon (story)' },
    { moment: 'Vrijdag', what: 'Een product of protocol in de spotlight (post)' },
    { moment: 'Elke maand', what: 'Nieuwsbrief met één artikel en één seizoenstip' },
    { moment: 'Na elke behandeling', what: 'Nazorgkaart mee, en de dag erna om een review vragen' },
  ],
}
